import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, formatRelative } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldAlert, ShieldCheck, Unlock, RefreshCw, Key, Smartphone, Mail,
  Pause, Play, Ban, LogOut, RotateCcw, Loader2,
  CheckCircle2, XCircle, Users, Server,
  KeyRound, Webhook, Fingerprint, Clock, Filter, Activity,
  MailX, MailPlus,
} from "lucide-react";
import "./AdminPartnerRecovery.css";

const MIN_REASON = 20;

const STATUS_TONE = {
  active: { bg: "#DCFCE7", fg: "#166534", label: "Active" },
  suspended: { bg: "#FEF3C7", fg: "#92400E", label: "Suspended" },
  emergency_disabled: { bg: "#FEE2E2", fg: "#991B1B", label: "Emergency disabled" },
  disabled: { bg: "#F1F5F9", fg: "#475569", label: "Disabled" },
  unknown: { bg: "#F1F5F9", fg: "#475569", label: "Unknown" },
};

// ─── Confirmation modal ──────────────────────────────────────────────────
function ConfirmModal({
  title, description, actionLabel, actionTone = "primary",
  requireTypedConfirm = false, extraFields = null,
  minReason = MIN_REASON, warningBanner = null,   // Iter 77.1 — escalation mode
  onSubmit, onClose, busy,
}) {
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [extras, setExtras] = useState({});

  const setExtra = (k, v) => setExtras((e) => ({ ...e, [k]: v }));

  const canSubmit =
    reason.length >= minReason &&
    (!requireTypedConfirm || confirmText === "CONFIRM") &&
    !busy;

  return (
    <div
      className="pr-modal__backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog" aria-modal="true"
      data-testid="pr-confirm-modal"
    >
      <div className="pr-modal">
        <header className="pr-modal__head">
          <div>
            <div className="pr-modal__eyebrow">
              <ShieldAlert className="w-3.5 h-3.5" /> Recovery action
            </div>
            <h3 className="pr-modal__title">{title}</h3>
          </div>
          <button onClick={onClose} className="pr-modal__close" aria-label="Close">
            <XCircle className="w-4 h-4" />
          </button>
        </header>

        <div className="pr-modal__body">
          <p className="pr-modal__desc">{description}</p>

          {warningBanner && (
            <div
              data-testid="pr-confirm-escalation-banner"
              style={{
                background: "#FFF7ED",
                border: "1px solid #FDBA74",
                color: "#9A3412",
                borderRadius: 8,
                padding: "0.6rem 0.8rem",
                fontSize: "0.82rem",
                lineHeight: 1.45,
                margin: "0.35rem 0 0.7rem",
                display: "flex",
                gap: "0.5rem",
                alignItems: "flex-start",
              }}
            >
              <ShieldAlert className="w-4 h-4" style={{ marginTop: 1, flexShrink: 0 }} />
              <span>{warningBanner}</span>
            </div>
          )}

          {extraFields && extraFields({ extras, setExtra })}

          <label className="pr-modal__label">
            Reason ({minReason}+ characters, becomes part of the immutable audit log)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g., Support ticket #12345 — customer verified identity via callback and photo ID."
            className="pr-modal__textarea"
            data-testid="pr-confirm-reason"
          />
          <div className="pr-modal__hint" data-testid="pr-confirm-reason-hint">
            {reason.length}/{minReason} characters minimum
          </div>

          {requireTypedConfirm && (
            <>
              <label className="pr-modal__label">
                Type <code>CONFIRM</code> to execute this high-risk action
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="CONFIRM"
                className="pr-modal__input"
                data-testid="pr-confirm-typed"
              />
            </>
          )}
        </div>

        <footer className="pr-modal__foot">
          <button onClick={onClose} className="pr-btn pr-btn--outline" disabled={busy}>
            Cancel
          </button>
          <button
            onClick={() => onSubmit(reason, extras)}
            disabled={!canSubmit}
            className={`pr-btn pr-btn--${actionTone}`}
            data-testid="pr-confirm-execute"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {actionLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ─── Recovery action wrapper ─────────────────────────────────────────────
function ActionButton({ icon: Icon, label, tone = "outline", onClick, testId, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`pr-action pr-action--${tone}`}
      data-testid={testId}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────
export default function AdminPartnerRecovery({ partnerId: partnerIdProp, embedded = false }) {
  const params = useParams();
  const partnerId = partnerIdProp || params.partnerId;
  const [dashboard, setDashboard] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [timelineFilter, setTimelineFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);   // { title, description, endpoint, requireTypedConfirm, needsUser, ... }
  const [busy, setBusy] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, credsRes, timelineRes] = await Promise.all([
        api.get(`/admin/partner-recovery/${partnerId}/dashboard`),
        api.get(`/admin/partner-recovery/${partnerId}/credentials`),
        api.get(`/admin/partner-recovery/${partnerId}/security-timeline?limit=200`),
      ]);
      setDashboard(dashRes.data);
      setCredentials(credsRes.data);
      setTimeline(timelineRes.data);
      if (!selectedUserId && dashRes.data?.users?.length) {
        setSelectedUserId(dashRes.data.users[0].id);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [partnerId, selectedUserId]);

  useEffect(() => { load(); }, [load]);

  // Load per-user invitation state whenever the selected user changes or
  // after any recovery action runs (triggered by `load()` bump below).
  useEffect(() => {
    if (!selectedUserId) { setInvitation(null); return; }
    let cancelled = false;
    api.get(`/admin/partner-recovery/${partnerId}/invitations/${selectedUserId}`)
      .then((r) => { if (!cancelled) setInvitation(r.data); })
      .catch(() => { if (!cancelled) setInvitation(null); });
    return () => { cancelled = true; };
  }, [partnerId, selectedUserId, dashboard]);

  const currentUser = useMemo(
    () => dashboard?.users?.find((u) => u.id === selectedUserId),
    [dashboard, selectedUserId]
  );

  const filteredTimeline = useMemo(() => {
    if (!timeline?.events) return [];
    if (timelineFilter === "all") return timeline.events;
    return timeline.events.filter((e) => e.category === timelineFilter);
  }, [timeline, timelineFilter]);

  const runAction = async (reason, extras) => {
    if (!modal) return;
    setBusy(true);
    try {
      const body = { reason, ...(modal.needsUser ? { partner_user_id: selectedUserId } : {}),
                       ...extras };
      const { data } = await api.post(modal.endpoint, body);
      toast.success(modal.successMessage || "Action completed");
      const revealValue = data?.new_key || data?.new_secret;
      if (revealValue) {
        setRevealedSecret({
          label: modal.revealLabel
            || (data.new_key ? "New API key" : "New webhook secret"),
          value: revealValue,
        });
      }
      setModal(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openAction = (config) => {
    if (config.needsUser && !selectedUserId) {
      toast.error("Select a partner user first");
      return;
    }
    setModal(config);
  };

  const orgStatus = dashboard?.organization?.status || "unknown";
  const orgTone = STATUS_TONE[orgStatus] || STATUS_TONE.unknown;

  return (
    <div className={`pr ${embedded ? "pr--embedded" : ""}`} data-testid="admin-partner-recovery">
      {/* Hero */}
      <div className="pr__hero">
        <div>
          {!embedded && (
            <Link to="/admin/partners" className="pr__breadcrumb">
              ← Partner Organizations
            </Link>
          )}
          <h1 className="pr__title">
            <ShieldAlert className="w-6 h-6" />
            Recovery &amp; Security Console
          </h1>
          <p className="pr__sub">
            {dashboard?.organization?.name || partnerId}
            {" · "}
            <span className="pr__status-pill"
                  style={{ background: orgTone.bg, color: orgTone.fg }}>
              {orgTone.label}
            </span>
          </p>
        </div>
        <button onClick={load} disabled={loading}
                className="pr-btn pr-btn--outline"
                data-testid="pr-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {loading && !dashboard ? (
        <div className="pr__loading"><Loader2 className="w-6 h-6 animate-spin" /> Loading…</div>
      ) : dashboard ? (
        <>
          {/* Status snapshot */}
          <section className="pr__section" data-testid="pr-status-snapshot">
            <h3 className="pr__section-title"><Server className="w-4 h-4" /> Status snapshot</h3>
            <div className="pr__grid">
              <StatusTile testId="pr-tile-portal" label="Portal status" value={orgTone.label} tone={orgStatus === "active" ? "ok" : "warn"} />
              <StatusTile testId="pr-tile-sessions" label="Active sessions" value={dashboard.portal?.active_sessions ?? 0} />
              <StatusTile testId="pr-tile-api" label="Active API keys" value={dashboard.api?.active_keys ?? 0} />
              <StatusTile testId="pr-tile-webhooks" label="Active webhooks" value={dashboard.webhook?.active ?? 0} />
              <StatusTile testId="pr-tile-mfa" label="MFA enrollment"
                          value={`${dashboard.mfa?.enrolled_users ?? 0}/${dashboard.mfa?.total_users ?? 0}`} />
              <StatusTile testId="pr-tile-oauth" label="OAuth clients" value={dashboard.oauth?.clients ?? 0} />
            </div>
            {dashboard.primary_admin && (
              <div className="pr__primary-admin">
                <Users className="w-3.5 h-3.5" />
                <span>Primary partner administrator: </span>
                <strong>{dashboard.primary_admin.name}</strong>
                <span> ({dashboard.primary_admin.email})</span>
              </div>
            )}
          </section>

          {/* User picker + user-scoped actions */}
          <section className="pr__section" data-testid="pr-user-actions">
            <div className="pr__section-head">
              <h3 className="pr__section-title"><Users className="w-4 h-4" /> Portal user recovery</h3>
              {dashboard.users?.length > 0 && (
                <select
                  value={selectedUserId || ""}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="pr-select"
                  data-testid="pr-user-select"
                >
                  {dashboard.users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.email} · {u.role}
                      {u.locked ? " · LOCKED" : ""}
                      {!u.mfa_enabled ? " · no MFA" : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {currentUser && (
              <div className="pr__user-card">
                <div className="pr__user-facts">
                  <Fact label="Email" value={currentUser.email} />
                  <Fact label="Role" value={currentUser.role} />
                  <Fact label="Status" value={currentUser.status} />
                  <Fact label="MFA" value={currentUser.mfa_enabled ? "Enrolled" : "None"} />
                  <Fact label="Password" value={currentUser.password_status || "active"} />
                  <Fact label="Activation" value={currentUser.activation_stage || "—"} />
                  <Fact label="Failed logins" value={currentUser.failed_login_count ?? 0} />
                  <Fact label="Locked?" value={currentUser.locked ? "Yes" : "No"} />
                  <Fact label="Last login" value={currentUser.last_login_at ? formatRelative(currentUser.last_login_at) : "—"} />
                  <Fact label="Last failed" value={currentUser.last_failed_login_at ? formatRelative(currentUser.last_failed_login_at) : "—"} />
                </div>
              </div>
            )}

            <div className="pr__actions-grid">
              <ActionButton icon={Unlock} label="Unlock account" testId="pr-btn-unlock"
                onClick={() => openAction({
                  title: "Unlock partner account",
                  description: "Clears the brute-force lockout for this user. Password and MFA are NOT changed. Existing sessions remain valid.",
                  endpoint: `/admin/partner-recovery/${partnerId}/unlock`,
                  needsUser: true,
                  successMessage: "Account unlocked",
                })} />
              <ActionButton icon={RotateCcw} label="Reset failed counter" testId="pr-btn-reset-counter"
                onClick={() => openAction({
                  title: "Reset failed login counter",
                  description: "Resets the failed authentication attempts back to zero for this user.",
                  endpoint: `/admin/partner-recovery/${partnerId}/reset-failed-counter`,
                  needsUser: true,
                  successMessage: "Counter reset",
                })} />
              <ActionButton icon={Key} label="Force password reset" testId="pr-btn-force-password"
                onClick={() => openAction({
                  title: "Force password reset",
                  description: "Expires the current password and emails the user a secure reset link (valid 24h). You will NOT see the new password.",
                  endpoint: `/admin/partner-recovery/${partnerId}/force-password-reset`,
                  needsUser: true,
                  successMessage: "Reset link emailed to user",
                })} />
              <ActionButton icon={Smartphone} label="Reset MFA" testId="pr-btn-reset-mfa"
                onClick={() => openAction({
                  title: "Reset multi-factor authentication",
                  description: "Removes the user's current MFA enrollment. They will be required to enroll a new authenticator on next login. No secrets are ever revealed.",
                  endpoint: `/admin/partner-recovery/${partnerId}/reset-mfa`,
                  needsUser: true,
                  successMessage: "MFA reset — user must re-enroll",
                })} />
              <ActionButton icon={Mail} label="Resend activation invite" testId="pr-btn-resend-invite"
                onClick={() => openAction({
                  title: "Resend activation invitation",
                  description: "Generates a fresh secure activation link and emails it to the user. Any previous invitation link is invalidated.",
                  endpoint: `/admin/partner-recovery/${partnerId}/resend-activation`,
                  needsUser: true,
                  successMessage: "Activation invite resent",
                })} />
            </div>

            {/* Iter 77.0 — Manage Invitation strip (revoke / reissue) */}
            {/* Iter 77.1 — Reissue Cooldown: when threshold reached the modal
                 switches to escalation mode (banner + 30-char reason gate + escalation=true). */}
            <ManageInvitation
              invitation={invitation}
              onRevoke={() => openAction({
                title: "Revoke pending invitation",
                description: "Immediately invalidates the outstanding activation link for this user. Anyone who received the previous email will get a &lsquo;revoked or superseded&rsquo; error if they click it. Use this if the invite email was mis-directed, the user is no longer authorized, or you want to pause activation.",
                endpoint: `/admin/partner-recovery/${partnerId}/invitations/${selectedUserId}/revoke`,
                needsUser: false,
                successMessage: "Invitation revoked",
              })}
              onReissue={() => {
                const cooldownActive = !!invitation?.reissue_cooldown_active;
                const minReason = cooldownActive
                  ? (invitation?.reissue_escalation_min_reason || 30)
                  : MIN_REASON;
                openAction({
                  title: cooldownActive
                    ? "Reissue invitation — Escalation required"
                    : "Reissue invitation",
                  description: cooldownActive
                    ? "This invitation has already been reissued the maximum number of times allowed in the current 24-hour window. A more detailed audit reason is required to override the cooldown."
                    : "Cancels the previous invitation link and emails a fresh one with a new 7-day expiry. Use this after revocation, when the previous link may have leaked, or when the user reports never receiving it.",
                  endpoint: `/admin/partner-recovery/${partnerId}/invitations/${selectedUserId}/reissue`,
                  needsUser: false,
                  successMessage: cooldownActive
                    ? "Fresh invitation emailed (cooldown bypassed — audited)"
                    : "Fresh invitation emailed",
                  minReason,
                  escalation: cooldownActive,
                  actionTone: cooldownActive ? "danger" : "primary",
                  warningBanner: cooldownActive
                    ? `This invitation has been reissued ${invitation?.reissue_count_in_window || 0} times in the last ${invitation?.reissue_window_hours || 24}h. An escalation reason of ${minReason}+ chars is required to send another. This action writes a dedicated cooldown-bypass audit event.`
                    : null,
                });
              }}
            />
          </section>

          {/* Organization actions */}
          <section className="pr__section" data-testid="pr-org-actions">
            <h3 className="pr__section-title"><ShieldCheck className="w-4 h-4" /> Organization actions</h3>
            <div className="pr__actions-grid">
              <ActionButton icon={LogOut} label="Force logout all sessions" testId="pr-btn-force-logout"
                onClick={() => openAction({
                  title: "Force logout every portal session",
                  description: "Terminates every active portal session for this organization. Users must re-authenticate. Passwords and MFA are unchanged.",
                  endpoint: `/admin/partner-recovery/${partnerId}/force-logout`,
                  successMessage: "All sessions terminated",
                })} />
              <ActionButton icon={Pause} label="Suspend account" tone="warning" testId="pr-btn-suspend"
                onClick={() => openAction({
                  title: "Suspend partner organization",
                  description: "Disables portal login, API access, OAuth, and webhook signing. Audit history, configuration, and organization data are preserved. Reversible.",
                  endpoint: `/admin/partner-recovery/${partnerId}/suspend`,
                  successMessage: "Organization suspended",
                })} />
              <ActionButton icon={Play} label="Reactivate account" testId="pr-btn-reactivate"
                onClick={() => openAction({
                  title: "Reactivate partner organization",
                  description: "Restores login, API access, OAuth, and webhooks. Credentials that were revoked during suspension are NOT auto-restored — the partner must generate new ones.",
                  endpoint: `/admin/partner-recovery/${partnerId}/reactivate`,
                  successMessage: "Organization reactivated",
                })} />
              <ActionButton icon={Ban} label="⚠ Emergency disable" tone="danger" testId="pr-btn-emergency"
                onClick={() => openAction({
                  title: "Emergency disable partner",
                  description: "Immediately disables EVERY authentication surface: portal, API keys, OAuth, webhooks, and active sessions. Reversible via reactivation but revoked credentials must be regenerated by the partner.",
                  endpoint: `/admin/partner-recovery/${partnerId}/emergency-disable`,
                  requireTypedConfirm: true,
                  extraFields: null,
                  successMessage: "Emergency disable executed",
                })} />
            </div>
          </section>

          {/* Credential rotation — Phase B */}
          <CredentialRotation
            partnerId={partnerId}
            credentials={credentials}
            onRotateKey={(k) => openAction({
              title: `Rotate API key: ${k.name}`,
              description: "Mints a replacement key with the same name, scopes, and rate limit, then revokes the old key. The new key is displayed ONE time — copy it and deliver via a secure channel. Any integration using the old key will start failing immediately.",
              endpoint: `/admin/partner-recovery/${partnerId}/regenerate-api-key/${k.id}`,
              successMessage: "API key rotated",
              revealLabel: "New API key",
            })}
            onRotateOAuth={(c) => openAction({
              title: `Rotate OAuth client secret: ${c.name}`,
              description: "Mints a replacement OAuth client secret, then revokes the previous one. The new secret is displayed ONE time — update your integration immediately. All access tokens issued by the old secret remain valid until they expire (max 30 min).",
              endpoint: `/admin/partner-recovery/${partnerId}/rotate-oauth-secret/${c.id}`,
              successMessage: "OAuth secret rotated",
              revealLabel: "New OAuth client secret",
            })}
            onRotateWebhook={(w) => openAction({
              title: `Rotate webhook signing secret`,
              description: `Generates a fresh HMAC signing secret for the webhook endpoint ${w.endpoint_url}. The previous secret is revoked immediately — deliveries signed with the old secret will fail signature verification on the partner side. Update the partner's endpoint before high-volume events resume.`,
              endpoint: `/admin/partner-recovery/${partnerId}/revoke-webhook-secret/${w.id}`,
              successMessage: "Webhook signing secret rotated",
              revealLabel: "New webhook signing secret",
            })}
          />

          {/* Security timeline — Phase C */}
          <SecurityTimeline
            timeline={timeline}
            filter={timelineFilter}
            setFilter={setTimelineFilter}
            filtered={filteredTimeline}
          />
        </>
      ) : null}

      {modal && (
        <ConfirmModal
          title={modal.title}
          description={modal.description}
          actionLabel={modal.actionLabel || "Execute"}
          actionTone={modal.actionTone || (modal.requireTypedConfirm ? "danger" : "primary")}
          requireTypedConfirm={modal.requireTypedConfirm}
          extraFields={modal.extraFields}
          minReason={modal.minReason || MIN_REASON}
          warningBanner={modal.warningBanner || null}
          onSubmit={(reason, extras) => {
            const body = { ...extras };
            if (modal.requireTypedConfirm) body.confirm = "CONFIRM";
            if (modal.escalation) body.escalation = true;
            runAction(reason, body);
          }}
          onClose={() => setModal(null)}
          busy={busy}
        />
      )}

      {revealedSecret && (
        <div className="pr-modal__backdrop" role="dialog" aria-modal="true"
             data-testid="pr-secret-modal"
             onClick={(e) => e.target === e.currentTarget && setRevealedSecret(null)}>
          <div className="pr-modal">
            <header className="pr-modal__head">
              <div>
                <div className="pr-modal__eyebrow"><Key className="w-3.5 h-3.5" /> One-time reveal</div>
                <h3 className="pr-modal__title">{revealedSecret.label}</h3>
              </div>
            </header>
            <div className="pr-modal__body">
              <p className="pr-modal__desc">
                This value will <strong>never</strong> be shown again. Copy it now and deliver it
                to the partner through a secure channel.
              </p>
              <div className="pr-secret" data-testid="pr-secret-value">
                <code>{revealedSecret.value}</code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(revealedSecret.value);
                    toast.success("Copied to clipboard");
                  }}
                  className="pr-btn pr-btn--outline pr-btn--sm"
                  data-testid="pr-secret-copy"
                >
                  Copy
                </button>
              </div>
            </div>
            <footer className="pr-modal__foot">
              <button
                onClick={() => setRevealedSecret(null)}
                className="pr-btn pr-btn--primary"
                data-testid="pr-secret-dismiss"
              >
                I&rsquo;ve saved it
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusTile({ label, value, tone = "neutral", testId }) {
  return (
    <div className="pr__tile" data-testid={testId}>
      <div className="pr__tile-label">{label}</div>
      <div className={`pr__tile-value pr__tile-value--${tone}`}>{value}</div>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="pr__fact">
      <div className="pr__fact-label">{label}</div>
      <div className="pr__fact-value">{value}</div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────
   Iter 77.0 — Manage Invitation (revoke / reissue pending invitations)
   ─────────────────────────────────────────────────────────────────────── */
function ManageInvitation({ invitation, onRevoke, onReissue }) {
  if (!invitation) return null;
  const {
    status, is_active, invite_jti_masked, invite_issued_at, invite_expires_at,
    invite_last_sent_at, invite_revoked_at, invite_revoked_by,
    invite_revoke_reason, invite_superseded_count,
    // Iter 77.1 — cooldown context (backward-compat: undefined ⇒ off)
    reissue_count_in_window, reissue_window_hours, reissue_standard_limit,
    reissue_cooldown_active, reissue_escalation_min_reason,
  } = invitation;

  // Only surface for users who are still going through activation, not
  // for fully-active portal users.
  if (status !== "invited" && status !== "invitation_revoked") return null;

  const expired = invite_expires_at && invite_expires_at < new Date().toISOString();

  let badge;
  if (invite_revoked_at) {
    badge = { text: "Revoked", bg: "#FEE2E2", fg: "#991B1B" };
  } else if (expired) {
    badge = { text: "Expired", bg: "#FEF3C7", fg: "#92400E" };
  } else if (is_active) {
    badge = { text: "Pending", bg: "#DBEAFE", fg: "#1E3A8A" };
  } else {
    badge = { text: "Legacy invite", bg: "#F1F5F9", fg: "#64748B" };
  }

  return (
    <div className="pr__invitation" data-testid="pr-manage-invitation">
      <div className="pr__invitation-head">
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <MailPlus className="w-3.5 h-3.5" style={{ color: "#1E3A8A" }} />
          <span style={{ fontSize: "0.78rem", fontWeight: 700,
                            color: "#1E3A8A", textTransform: "uppercase",
                            letterSpacing: "0.05em" }}>
            Manage invitation
          </span>
          <span className="pr__status-pill"
                style={{ background: badge.bg, color: badge.fg, marginLeft: "0.35rem" }}
                data-testid="pr-invitation-badge">
            {badge.text}
          </span>
          {reissue_cooldown_active && (
            <span
              className="pr__status-pill"
              data-testid="pr-invitation-cooldown-pill"
              style={{ background: "#FFF7ED", color: "#9A3412",
                        border: "1px solid #FDBA74", marginLeft: "0.25rem" }}
              title={`${reissue_count_in_window}/${reissue_standard_limit} reissues in the last ${reissue_window_hours}h`}
            >
              Cooldown
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: "0.35rem" }}>
          <button
            className={`pr-btn pr-btn--sm ${reissue_cooldown_active ? "pr-btn--warning" : "pr-btn--outline"}`}
            onClick={onReissue}
            data-testid="pr-invitation-reissue"
          >
            <RotateCcw className="w-3 h-3" />
            {reissue_cooldown_active ? "Reissue (escalation)" : "Reissue"}
          </button>
          <button
            className="pr-btn pr-btn--danger pr-btn--sm"
            onClick={onRevoke}
            disabled={!is_active}
            data-testid="pr-invitation-revoke"
            title={!is_active ? "No active invitation to revoke" : "Revoke this invitation"}
          >
            <MailX className="w-3 h-3" /> Revoke
          </button>
        </div>
      </div>

      {reissue_cooldown_active && (
        <div
          data-testid="pr-invitation-cooldown-warning"
          style={{
            background: "#FFF7ED",
            border: "1px solid #FDBA74",
            color: "#9A3412",
            borderRadius: 8,
            padding: "0.55rem 0.75rem",
            fontSize: "0.78rem",
            lineHeight: 1.45,
            margin: "0.5rem 0",
            display: "flex",
            gap: "0.5rem",
            alignItems: "flex-start",
          }}
        >
          <ShieldAlert className="w-3.5 h-3.5" style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            This invitation has been reissued <strong>{reissue_count_in_window}</strong> times in the
            last <strong>{reissue_window_hours}h</strong>. An escalation reason of{" "}
            <strong>{reissue_escalation_min_reason || 30}+ chars</strong> is required to send another.
          </span>
        </div>
      )}

      <div className="pr__invitation-facts">
        <Fact label="Issued"
              value={invite_issued_at ? formatRelative(invite_issued_at) : "—"} />
        <Fact label="Expires"
              value={invite_expires_at ? formatRelative(invite_expires_at) : "—"} />
        <Fact label="Last sent"
              value={invite_last_sent_at ? formatRelative(invite_last_sent_at) : "—"} />
        <Fact label="Token id" value={<code data-testid="pr-invitation-jti">
          {invite_jti_masked || "—"}
        </code>} />
        <Fact label="Superseded"
              value={<span data-testid="pr-invitation-superseded">
                {invite_superseded_count || 0} time{(invite_superseded_count || 0) === 1 ? "" : "s"}
              </span>} />
        {typeof reissue_count_in_window === "number" && (
          <Fact
            label="Reissues (24h)"
            value={<span data-testid="pr-invitation-reissue-count">
              {reissue_count_in_window}/{reissue_standard_limit ?? 3}
            </span>}
          />
        )}
      </div>
      {invite_revoked_at && (
        <div className="pr__invitation-revoked" data-testid="pr-invitation-revoked-note">
          Revoked {formatRelative(invite_revoked_at)}
          {invite_revoked_by ? ` by ${invite_revoked_by}` : ""}.
          {invite_revoke_reason
            ? <> Reason: <em>{invite_revoke_reason}</em></>
            : null}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────
   Phase B — Credential rotation
   ─────────────────────────────────────────────────────────────────────── */
function CredentialRotation({ partnerId, credentials, onRotateKey, onRotateOAuth, onRotateWebhook }) {
  const c = credentials || { api_keys: [], oauth_clients: [], webhooks: [] };
  const [tab, setTab] = useState("api");
  const tabs = [
    { key: "api",     label: "API Keys",       icon: KeyRound,    count: c.api_keys?.length || 0, testId: "pr-creds-tab-api" },
    { key: "oauth",   label: "OAuth Clients",  icon: Fingerprint, count: c.oauth_clients?.length || 0, testId: "pr-creds-tab-oauth" },
    { key: "webhook", label: "Webhooks",       icon: Webhook,     count: c.webhooks?.length || 0, testId: "pr-creds-tab-webhook" },
  ];
  return (
    <section className="pr__section" data-testid="pr-creds-section">
      <h3 className="pr__section-title">
        <Fingerprint className="w-4 h-4" /> Credential rotation
      </h3>
      <div style={{ display: "flex", gap: "0.35rem", borderBottom: "1px solid #E2E8F0", marginBottom: "0.85rem" }}>
        {tabs.map(({ key, label, icon: Icon, count, testId }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={testId}
              style={{
                display: "inline-flex", alignItems: "center", gap: "0.4rem",
                padding: "0.5rem 0.85rem", border: "none", background: "transparent",
                borderBottom: active ? "2px solid #1E3A8A" : "2px solid transparent",
                color: active ? "#1E3A8A" : "#64748B",
                fontWeight: active ? 700 : 500, fontSize: "0.78rem", cursor: "pointer",
              }}
            >
              <Icon className="w-3.5 h-3.5" /> {label} <span style={{
                background: active ? "#1E3A8A" : "#E2E8F0",
                color: active ? "#FFFFFF" : "#475569",
                padding: "0.05rem 0.4rem", borderRadius: "999px", fontSize: "0.68rem",
              }}>{count}</span>
            </button>
          );
        })}
      </div>

      {tab === "api" && (
        <CredentialTable
          testId="pr-creds-table-api"
          rows={c.api_keys}
          columns={["Name", "Prefix", "Scopes", "Requests", "Last used", "Status", ""]}
          renderRow={(k) => (
            <tr key={k.id} data-testid={`pr-api-key-row-${k.id}`}>
              <td>{k.name}</td>
              <td><code>gv_{k.prefix}…</code></td>
              <td>{k.scopes.length}</td>
              <td>{k.request_count}</td>
              <td style={{ color: "#64748B" }}>{k.last_used_at ? formatRelative(k.last_used_at) : "—"}</td>
              <td>{k.revoked
                ? <span style={{ color: "#991B1B", fontSize: "0.72rem", fontWeight: 600 }}>REVOKED</span>
                : <span style={{ color: "#166534", fontSize: "0.72rem", fontWeight: 600 }}>ACTIVE</span>}</td>
              <td style={{ textAlign: "right" }}>
                {!k.revoked && (
                  <button className="pr-btn pr-btn--outline pr-btn--sm"
                          onClick={() => onRotateKey(k)}
                          data-testid={`pr-rotate-api-${k.id}`}>
                    <RotateCcw className="w-3 h-3" /> Rotate
                  </button>
                )}
              </td>
            </tr>
          )}
          emptyText="No API keys."
        />
      )}

      {tab === "oauth" && (
        <CredentialTable
          testId="pr-creds-table-oauth"
          rows={c.oauth_clients}
          columns={["Name", "Client ID prefix", "Scopes", "Last used", "Status", ""]}
          renderRow={(o) => (
            <tr key={o.id} data-testid={`pr-oauth-row-${o.id}`}>
              <td>{o.name}</td>
              <td><code>gv_{o.prefix}…</code></td>
              <td>{o.scopes.length}</td>
              <td style={{ color: "#64748B" }}>{o.last_used_at ? formatRelative(o.last_used_at) : "—"}</td>
              <td>{o.revoked
                ? <span style={{ color: "#991B1B", fontSize: "0.72rem", fontWeight: 600 }}>REVOKED</span>
                : <span style={{ color: "#166534", fontSize: "0.72rem", fontWeight: 600 }}>ACTIVE</span>}</td>
              <td style={{ textAlign: "right" }}>
                {!o.revoked && (
                  <button className="pr-btn pr-btn--outline pr-btn--sm"
                          onClick={() => onRotateOAuth(o)}
                          data-testid={`pr-rotate-oauth-${o.id}`}>
                    <RotateCcw className="w-3 h-3" /> Rotate secret
                  </button>
                )}
              </td>
            </tr>
          )}
          emptyText="No OAuth clients."
        />
      )}

      {tab === "webhook" && (
        <CredentialTable
          testId="pr-creds-table-webhook"
          rows={c.webhooks}
          columns={["Endpoint", "Events", "Delivered", "Failed", "Rotated", "Status", ""]}
          renderRow={(w) => (
            <tr key={w.id} data-testid={`pr-webhook-row-${w.id}`}>
              <td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }} title={w.endpoint_url}>
                <code style={{ fontSize: "0.72rem" }}>{w.endpoint_url}</code>
              </td>
              <td>{w.event_types.length}</td>
              <td>{w.delivery_count}</td>
              <td>{w.failure_count}</td>
              <td style={{ color: "#64748B" }}>{w.signing_secret_rotated_at ? formatRelative(w.signing_secret_rotated_at) : "—"}</td>
              <td>{w.active
                ? <span style={{ color: "#166534", fontSize: "0.72rem", fontWeight: 600 }}>ACTIVE</span>
                : <span style={{ color: "#64748B", fontSize: "0.72rem", fontWeight: 600 }}>INACTIVE</span>}</td>
              <td style={{ textAlign: "right" }}>
                <button className="pr-btn pr-btn--outline pr-btn--sm"
                        onClick={() => onRotateWebhook(w)}
                        data-testid={`pr-rotate-webhook-${w.id}`}>
                  <RotateCcw className="w-3 h-3" /> Rotate secret
                </button>
              </td>
            </tr>
          )}
          emptyText="No webhook subscriptions."
        />
      )}
    </section>
  );
}

function CredentialTable({ testId, rows, columns, renderRow, emptyText }) {
  return (
    <div data-testid={testId}>
      {(!rows || rows.length === 0) ? (
        <div style={{ padding: "0.75rem 0", color: "#64748B", fontSize: "0.85rem" }}>{emptyText}</div>
      ) : (
        <table className="pr__cred-table">
          <thead>
            <tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr>
          </thead>
          <tbody>{rows.map(renderRow)}</tbody>
        </table>
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────
   Phase C — Security timeline
   ─────────────────────────────────────────────────────────────────────── */
const CATEGORY_LABELS = {
  all:          { label: "All events",     tone: "info" },
  login:        { label: "Successful logins", tone: "ok" },
  failed_login: { label: "Failed logins",  tone: "warn" },
  recovery:     { label: "Recovery actions", tone: "danger" },
  logout:       { label: "Logouts",        tone: "info" },
};

const SEVERITY_TONE = {
  info:   { bg: "#EFF6FF", fg: "#1E3A8A", label: "Info" },
  ok:     { bg: "#DCFCE7", fg: "#166534", label: "OK" },
  warn:   { bg: "#FEF3C7", fg: "#92400E", label: "Warn" },
  danger: { bg: "#FEE2E2", fg: "#991B1B", label: "Danger" },
};

const ACTION_LABELS = {
  partner_login: "Successful login",
  partner_login_failed: "Failed login attempt",
  partner_logout: "Portal logout",
  partner_mfa_verify: "MFA verified",
  partner_mfa_verify_failed: "MFA verification failed",
  partner_recovery_unlock: "Account unlocked",
  partner_recovery_reset_failed_counter: "Failed counter reset",
  partner_recovery_force_password_reset: "Password reset forced",
  partner_recovery_reset_mfa: "MFA reset",
  partner_recovery_resend_activation: "Activation invitation resent",
  partner_recovery_suspend: "Organization suspended",
  partner_recovery_reactivate: "Organization reactivated",
  partner_recovery_emergency_disable: "Emergency disable executed",
  partner_recovery_force_logout: "All sessions forced logout",
  partner_recovery_regenerate_api_key: "API key rotated",
  partner_recovery_rotate_oauth_secret: "OAuth client secret rotated",
  partner_recovery_revoke_webhook_secret: "Webhook signing secret rotated",
  partner_recovery_self_service_lockdown: "Self-service lockdown (partner-initiated)",
};

function SecurityTimeline({ timeline, filter, setFilter, filtered }) {
  const counts = timeline?.counts_by_category || {};
  return (
    <section className="pr__section" data-testid="pr-timeline-section">
      <div className="pr__section-head">
        <h3 className="pr__section-title">
          <Activity className="w-4 h-4" /> Security timeline
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
          <Filter className="w-3.5 h-3.5" style={{ color: "#64748B" }} />
          {["all", "login", "failed_login", "recovery", "logout"].map((cat) => {
            const cfg = CATEGORY_LABELS[cat];
            const active = filter === cat;
            const n = cat === "all"
              ? (timeline?.count ?? 0)
              : (counts[cat] || 0);
            return (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`pr-timeline-filter-${cat}`}
                style={{
                  padding: "0.25rem 0.6rem", borderRadius: "999px",
                  border: `1px solid ${active ? "#1E3A8A" : "#CBD5E1"}`,
                  background: active ? "#1E3A8A" : "#FFFFFF",
                  color: active ? "#FFFFFF" : "#334155",
                  fontSize: "0.72rem", fontWeight: 600, cursor: "pointer",
                }}
              >
                {cfg.label} · {n}
              </button>
            );
          })}
        </div>
      </div>

      {(!filtered || filtered.length === 0) ? (
        <div style={{ padding: "0.75rem 0", color: "#64748B", fontSize: "0.85rem" }}
             data-testid="pr-timeline-empty">
          No events for the selected filter yet.
        </div>
      ) : (
        <div className="pr__timeline" data-testid="pr-timeline-list">
          {filtered.map((e, idx) => {
            const tone = SEVERITY_TONE[e.severity] || SEVERITY_TONE.info;
            return (
              <div key={e.correlation_id || `${e.timestamp}-${idx}`}
                   className="pr__timeline-item"
                   data-testid={`pr-timeline-event-${idx}`}>
                <div
                  className="pr__timeline-dot"
                  style={{ background: tone.fg }}
                  aria-hidden="true"
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap" }}>
                    <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.85rem" }}>
                      {ACTION_LABELS[e.action] || e.action}
                    </div>
                    <div style={{ fontSize: "0.7rem", color: "#64748B", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                      <span className="pr__status-pill"
                            style={{ background: tone.bg, color: tone.fg }}>{tone.label}</span>
                      <Clock className="w-3 h-3" />
                      {e.timestamp ? formatRelative(e.timestamp) : "—"}
                    </div>
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#475569", marginTop: "0.2rem" }}>
                    {e.actor_email ? (
                      <span>by <strong>{e.actor_name || e.actor_email}</strong>{e.actor_role ? ` (${e.actor_role})` : ""}</span>
                    ) : (
                      <span style={{ color: "#94A3B8" }}>system</span>
                    )}
                  </div>
                  {e.reason && (
                    <div style={{ fontSize: "0.75rem", color: "#0F172A", marginTop: "0.25rem",
                                    background: "#F8FAFC", padding: "0.35rem 0.55rem",
                                    borderLeft: `3px solid ${tone.fg}`, borderRadius: 4 }}>
                      {e.reason}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
